/* eslint-disable react/prop-types */
import Badge, { BadgeVariant } from 'components/Badge'
import { ButtonPrimary } from 'components/Button'
import { LightCard, OutlineCard } from 'components/Card'
import Column, { AutoColumn } from 'components/Column'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import Loader from 'components/Loader'
import Row, { RowBetween, RowFixed } from 'components/Row'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import TimeBetweenText from 'components/TimeBetweenText'
import { KodiakFarmStake } from 'hooks/useKodiakFarm'
import { useRewardVaultDatas } from 'hooks/useRewardVault'
import sumCurrencyAmount from 'lib/utils/sumCurrencyAmount'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useOnVaultUnstakeCallback } from 'pages/Islands/IslandPage/IslandManagementCard/hooks/useOnUnstakeCallback'
import WithdrawFromRewardVaultStep from 'pages/Islands/IslandPage/IslandManagementCard/steps/WithdrawFromRewardVaultStep'
import { useContext, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Lock, Unlock } from 'react-feather'
import { Text } from 'rebass'
import styled, { ThemeContext } from 'styled-components'
import { Separator } from 'theme'

import { SharedProps } from '.'
import { useOnUnstakeCallback } from './hooks/useOnUnstakeCallback'
import WithdrawBalanceConstructorStep from './steps/WithdrawBalanceConstructorStep'
import WithdrawFromStakeStep from './steps/WithdrawFromStakeStep'
import { UnstakeManualUnlockButton } from './styleds'

const StyledController = styled(OutlineCard)<{ active?: boolean; pulsing?: boolean; disabled?: boolean }>`
  border-color: ${({ active, theme }) => (active ? theme.deprecated_blue1 : theme.cardBorderPrimary)};
  border-radius: 8px;
  height: 32px;
  width: 32px;

  background: ${({ theme }) => theme.cardPrimary};
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ disabled, theme }) =>
    !disabled
      ? `
    :hover {
      border-color: ${theme.deprecated_blue1};
      background: ${theme.deprecated_blue1};
    }
    cursor: pointer;
  `
      : `
    opacity: 0.5;
  `}

  overflow: hidden;

  user-select: none;
`
type PropsOf<T> = T extends React.ComponentType<infer P> ? P : never

const Controller = ({
  children,
  onMouseDown,
  onMouseUp,
  onPress,
  onClick: _,
  ...props
}: { children: any; onPress: () => void } & PropsOf<typeof StyledController>) => {
  const [pressing, setPressing] = useState(false)

  const handleMouseDown = (e: any) => {
    setPressing(true)
    return onMouseDown && onMouseDown(e)
  }
  const handleMouseUp = (e: any) => {
    setPressing(false)
    return onMouseUp && onMouseUp(e)
  }

  useEffect(() => {
    if (pressing) {
      let throttling = 7
      let tick = -1
      let skips = 5

      const int = setInterval(() => {
        if (skips > 0) {
          skips--
          return
        }

        tick++
        if (throttling !== 0 && tick % throttling !== 0) {
          return
        }
        onPress()
        if (throttling > 0) {
          throttling = throttling - 1
        }
      }, 75)

      return () => {
        clearInterval(int)
      }
    }
    return undefined
  }, [pressing])

  return (
    <StyledController {...props} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} onClick={onPress}>
      {children}
    </StyledController>
  )
}

const UnstakeView = ({
  farm,
  token,
  lpBalance,
  totalLiquidity,
  avaliableStakes,
  stakes,
  stakingType = 'farm',
  rewardVault,
  showMigrationOption,
}: SharedProps) => {
  const [value, setValue] = useState('')
  const theme = useContext(ThemeContext)

  // Get reward vault contract and data if necessary
  const { data: vaultData } = useRewardVaultDatas(stakingType === 'vault' ? rewardVault : undefined)

  // For vault staking
  const [vaultUnstakeAmount, setVaultUnstakeAmount] = useState('')
  const typedVaultAmount = useMemo(
    () => tryParseCurrencyAmount(vaultUnstakeAmount, token || undefined),
    [vaultUnstakeAmount, token]
  )

  // Validation for vault unstaking
  const isInvalidAmount = useMemo(() => {
    if (stakingType !== 'vault' || !vaultData?.userStaked || !typedVaultAmount) return false
    return typedVaultAmount.greaterThan(vaultData.userStaked)
  }, [vaultData?.userStaked, typedVaultAmount, stakingType])

  // Farm staking
  const unavaliableStakes = useMemo(() => {
    if (stakingType !== 'farm' || !stakes) return []
    return stakes.filter((item) => item.endingTimestamp.getTime() > Date.now())
  }, [stakes, stakingType])

  const sortedAvailableStakes = useMemo(
    () =>
      stakingType === 'farm' && avaliableStakes
        ? avaliableStakes
            .filter((item) => !!item.liquidity)
            .sort((a, b) => (b.liquidity!.greaterThan(a.liquidity!) ? -1 : 1))
        : [],
    [avaliableStakes, stakingType]
  )

  const [index, setIndex] = useState(-1)
  const [manualIndexes, setManualIndexes] = useState<number[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isVaultModalOpen, setIsVaultModalOpen] = useState(false)

  const plus = () => {
    if (!sortedAvailableStakes || stakingType !== 'farm') return

    if (index < sortedAvailableStakes.length - 1) {
      setIndex((prev) => {
        if (prev < sortedAvailableStakes.length - 1) {
          const newIndex = prev + 1
          return newIndex
        }
        return prev
      })
    }
  }

  const Stake = ({
    disabled,
    stake: { liquidity, endingTimestamp, lockMultiplier, kekId },
    onSelect,
  }: {
    stake: KodiakFarmStake
    disabled?: boolean
    onSelect?: () => void
  }) => {
    const isLocked = endingTimestamp.getTime() > Date.now()

    return (
      <RowBetween opacity={disabled ? 0.5 : 1}>
        <RowFixed marginLeft="4px">
          <Text marginLeft="4px" marginRight="4px" fontSize={12}>
            {liquidity ? liquidity.toExact() : ''} {token?.symbol}
          </Text>

          <Badge variant={BadgeVariant.PRIMARY} size="sm">
            <Text fontSize={10} opacity={0.5}>
              x{lockMultiplier.toFixed(2)}
            </Text>
          </Badge>
        </RowFixed>

        <RowFixed>
          {isLocked ? (
            <Lock color={theme.accentFailure} size={14} opacity={0.6} />
          ) : (
            <UnstakeManualUnlockButton clickable={onSelect !== undefined} onClick={() => onSelect?.()}>
              <Unlock color={disabled ? theme.textSecondary : theme.accentSuccess} size={14} opacity={0.6} />
            </UnstakeManualUnlockButton>
          )}
          {isLocked && (
            <Text fontSize={12} marginLeft="4px">
              <TimeBetweenText date={endingTimestamp}>
                <Loader />
              </TimeBetweenText>
            </Text>
          )}
        </RowFixed>
      </RowBetween>
    )
  }

  const minus = () => {
    if (!sortedAvailableStakes || stakingType !== 'farm') return

    if (index - 1 >= -1) {
      setIndex((prev) => {
        const newIndex = prev - 1
        if (newIndex >= -1) {
          return newIndex
        }
        return prev
      })
    }
  }

  const selectedAvailableStakes = useMemo(() => {
    if (!sortedAvailableStakes || stakingType !== 'farm') return []
    const combined = [
      ...sortedAvailableStakes.slice(0, index + 1),
      ...manualIndexes.map((index) => sortedAvailableStakes[index]).filter(Boolean),
    ]
    const uniqueArray = combined.filter((item, index) => {
      const _index = combined.findIndex((i) => i?.kekId === item?.kekId)
      return _index === index
    })

    return uniqueArray
  }, [index, sortedAvailableStakes, manualIndexes, stakingType])

  const selectedShares = useMemo(() => {
    return sumCurrencyAmount(selectedAvailableStakes.map((item) => item.liquidity))
  }, [selectedAvailableStakes])

  const totalAvailableStakes = useMemo(() => {
    if (!sortedAvailableStakes || stakingType !== 'farm') {
      return undefined
    }
    return sumCurrencyAmount(sortedAvailableStakes.map((item) => item!.liquidity!))
  }, [sortedAvailableStakes, stakingType])

  useEffect(() => {
    if (!selectedShares) {
      setValue('0')
      return
    }
    setValue(selectedShares.toExact() || '0')
  }, [selectedShares])

  const [selectedStakes, setSelectedStakes] = useState<KodiakFarmStake[] | undefined>(undefined)
  const constructorStep = useStepWithNext('WithdrawBalanceConstructorStep', WithdrawBalanceConstructorStep, {
    selectedShares,
    stakes: sortedAvailableStakes,
    onSelect: ({ stakes }) => setSelectedStakes(stakes),
    defaultActiveStakes: selectedAvailableStakes,
    farm,
    stakingType,
  })

  // Farm callbacks
  const {
    onConfirmWithdrawStakes: onConfirmFarmWithdraw,
    attemptingTxn: attemptingFarmTxn,
    hash: farmHash,
    clear: clearFarm,
  } = useOnUnstakeCallback(selectedStakes || [], farm?.address, () => {
    setIndex(-1)
    setManualIndexes([])
    setIsOpen(false)
  })

  // Vault callbacks
  const {
    onConfirmWithdrawStakes: onConfirmVaultWithdraw,
    attemptingTxn: attemptingVaultTxn,
    hash: vaultHash,
    clear: clearVault,
  } = useOnVaultUnstakeCallback(typedVaultAmount, stakingType === 'vault' ? rewardVault : undefined, () => {
    setVaultUnstakeAmount('')
    setIsVaultModalOpen(false)
  })

  // Create steps for farm and vault
  const farmWithdrawStep = useStepWithNext('WithdrawFromStakeStep', WithdrawFromStakeStep, {
    stakes: selectedStakes || [],
    onWithdraw: onConfirmFarmWithdraw,
    farm,
    hash: farmHash,
    attemptingTxn: attemptingFarmTxn,
    pendingText: (
      <>
        Unstaking {selectedShares?.toSignificant(6)} {token?.symbol}
      </>
    ),
    autoConfirm: true,
    transactionWaiting: true,
    stakingType: 'farm',
  })

  // For vault, create a fake stake that matches the KodiakFarmStake interface
  // const vaultStake = useMemo(() => {
  //   if (!typedVaultAmount || typedVaultAmount.equalTo('0') || !token) return undefined

  //   return [
  //     {
  //       id: `vault-stake-${rewardVault}`,
  //       kekId: `vault-stake-${rewardVault}`,
  //       liquidity: typedVaultAmount,
  //       endingTimestamp: new Date(0),
  //       lockMultiplier: { toFixed: () => '1.00' },
  //     },
  //   ]
  // }, [typedVaultAmount, rewardVault, token])

  const vaultWithdrawStep = useStepWithNext('WithdrawFromRewardVaultStep', WithdrawFromRewardVaultStep, {
    amount: typedVaultAmount,
    rewardVault,
    onWithdraw: onConfirmVaultWithdraw,
    hash: vaultHash,
    attemptingTxn: attemptingVaultTxn,
    pendingText: (
      <>
        Unstaking {typedVaultAmount?.toSignificant(6) || '0'} {token?.symbol}
      </>
    ),
    autoConfirm: true,
    transactionWaiting: true,
    stakingType: 'vault',
  })

  if (stakingType === 'vault') {
    return (
      <>
        <StepLineModal
          steps={[vaultWithdrawStep]}
          isOpen={isVaultModalOpen}
          header={<>Unstake from Vault</>}
          onDismiss={clearVault}
          onFinish={clearVault}
        />

        {vaultData?.userStaked && !vaultData.userStaked.equalTo('0') ? (
          <AutoColumn gap="md">
            <LightCard>
              <AutoColumn gap="md">
                <RowBetween>
                  <RowFixed>
                    <Text fontSize={14} fontWeight={500} color={theme.textSecondary}>
                      Currently Staked:
                    </Text>
                  </RowFixed>
                  <RowFixed>
                    <Text fontSize={14} fontWeight={500} marginRight="8px">
                      {vaultData.userStaked.toSignificant(6)} {vaultData.userStaked.currency.symbol}
                    </Text>
                    {/* <DoubleCurrencyLogo currency0={token0 || undefined} currency1={token1 || undefined} size={16} /> */}
                  </RowFixed>
                </RowBetween>
              </AutoColumn>
            </LightCard>

            <CurrencyInputPanel
              value={vaultUnstakeAmount}
              onUserInput={setVaultUnstakeAmount}
              onMax={() => {
                if (vaultData?.userStaked) {
                  setVaultUnstakeAmount(vaultData.userStaked.toExact() || '0')
                }
              }}
              renderBalance={() => <>{vaultData.userStaked?.toExact()}</>}
              locked={showMigrationOption}
              lockedMessage="Complete migration to access unstaking"
              hideBalance={false}
              showMaxButton={true}
              currency={token}
              id="vault-unstake-amount"
              showCommonBases
              label="Amount to Unstake"
            />

            {typedVaultAmount && !typedVaultAmount.equalTo('0') && (
              <LightCard>
                <AutoColumn gap="md">
                  <RowBetween>
                    <Text fontSize={16} fontWeight={500}>
                      Amount to Unstake:
                    </Text>
                    <RowFixed>
                      <Text fontSize={16} fontWeight={500} marginLeft="6px">
                        {typedVaultAmount.toSignificant(6)} {token?.symbol}
                      </Text>
                    </RowFixed>
                  </RowBetween>
                </AutoColumn>
              </LightCard>
            )}

            <ButtonPrimary
              disabled={!typedVaultAmount || typedVaultAmount.equalTo('0') || isInvalidAmount}
              onClick={() => setIsVaultModalOpen(true)}
            >
              {isInvalidAmount ? (
                'Insufficient staked amount'
              ) : !typedVaultAmount || typedVaultAmount.equalTo('0') ? (
                'Enter unstake amount'
              ) : (
                <>
                  Unstake {typedVaultAmount.toSignificant(6)} {token?.symbol}
                </>
              )}
            </ButtonPrimary>
          </AutoColumn>
        ) : (
          <AutoColumn gap="md">
            <LightCard>
              <Text fontSize={16} textAlign="center" color={theme.textSecondary}>
                You don&apos;t have any tokens staked in this vault.
              </Text>
            </LightCard>
          </AutoColumn>
        )}
      </>
    )
  }

  // Default Farm view
  return (
    <>
      <StepLineModal
        steps={[constructorStep, farmWithdrawStep]}
        isOpen={isOpen}
        header={<>Unstake</>}
        onDismiss={clearFarm}
        onFinish={clearFarm}
      />
      {!!(unavaliableStakes.length || sortedAvailableStakes?.length) && (
        <>
          <LightCard>
            <AutoColumn gap="md">
              {unavaliableStakes.map((stake) => (
                <Stake key={stake.kekId} stake={stake} />
              ))}
              {!!(unavaliableStakes.length && sortedAvailableStakes?.length) && <Separator />}
              {sortedAvailableStakes?.map((stake, currentIndex) => (
                <Stake
                  key={stake.kekId}
                  stake={stake}
                  disabled={!selectedAvailableStakes.some((item) => item.kekId === stake.kekId)}
                  onSelect={() => {
                    if (manualIndexes.includes(currentIndex)) {
                      setManualIndexes((prev) => prev.filter((item) => item !== currentIndex))
                    } else {
                      setManualIndexes((prev) => [...prev, currentIndex])
                    }
                  }}
                />
              ))}
            </AutoColumn>
          </LightCard>
          <Separator />
        </>
      )}

      <AutoColumn gap="md" style={{ position: 'relative' }}>
        <LightCard opacity={avaliableStakes?.length ? 1 : 0.5}>
          <RowBetween>
            <RowFixed grow="1" style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
              <Column>
                <Row align="end">
                  <Text fontSize={16} color={theme.textPrimary}>
                    {value}
                  </Text>
                  <Text
                    fontSize={16}
                    color={theme.textSecondary}
                    opacity={0.5}
                    hidden={selectedShares ? totalAvailableStakes?.equalTo(selectedShares) : false}
                  >
                    {totalAvailableStakes && <>/{totalAvailableStakes?.toSignificant(6)}</>}
                  </Text>
                </Row>
                <Text fontSize={12} color={theme.textSecondary}>
                  {token?.symbol}
                </Text>
              </Column>
            </RowFixed>

            <RowFixed grow="0" shrink="0">
              <Controller disabled={index === -1} marginRight="4px" onPress={minus}>
                -
              </Controller>
              <Controller disabled={index === (sortedAvailableStakes?.length || 0) - 1} onPress={plus}>
                +
              </Controller>
            </RowFixed>
          </RowBetween>
        </LightCard>

        <ButtonPrimary disabled={selectedAvailableStakes.length === 0} onClick={() => setIsOpen(true)}>
          {(avaliableStakes?.length || 0) === 0 ? (
            <>
              <AlertTriangle size={16} color={theme.accentWarning}></AlertTriangle>
              <Text fontSize={14} marginLeft="4px" color={theme.accentWarning}>
                There are no available stakes
              </Text>
            </>
          ) : (
            <>Unstake {selectedAvailableStakes.length >= 0 && <>{selectedAvailableStakes.length} positions</>}</>
          )}
        </ButtonPrimary>
      </AutoColumn>
    </>
  )
}

export default UnstakeView
